// Client for the per-clinic outreach program (/outreach/org/{org}/program*)
// and the clinic calls dashboard (/outreach/org/{org}/dashboard).
// Every route requires ops access to the organization.

import type { ArmResults } from "@/features/outreach/types/outreach.types"
import { apiCall } from "./apcm-ops.api"
import type {
  ClinicDashboard,
  DisableResult,
  PlanEntries,
  ProgramConfigPatch,
  ProgramMode,
  ProgramStatus,
  RestartRequest,
  RestartResult,
  TickRecord,
} from "../types/outreach-program.types"

const base = (orgId: string) => `/outreach/org/${orgId}/program`

export function getProgram(orgId: string) {
  return apiCall<ProgramStatus>({ method: "GET", url: base(orgId) })
}

export function updateProgramConfig(orgId: string, config: ProgramConfigPatch) {
  return apiCall<ProgramStatus>({ method: "PATCH", url: base(orgId), data: { config } })
}

export function enableProgram(orgId: string, mode: ProgramMode) {
  return apiCall<ProgramStatus>({ method: "POST", url: `${base(orgId)}/enable`, data: { mode } })
}

export function shadowProgram(orgId: string) {
  return apiCall<ProgramStatus>({ method: "POST", url: `${base(orgId)}/shadow` })
}

export function pauseProgram(orgId: string, reason: string) {
  return apiCall<ProgramStatus>({ method: "POST", url: `${base(orgId)}/pause`, data: { reason } })
}

export function disableProgram(orgId: string, reason: string) {
  return apiCall<DisableResult>({ method: "POST", url: `${base(orgId)}/disable`, data: { reason } })
}

/** D3: dry-run by default; pass dry_run=false only after the operator confirmed the preview. */
export function restartMaxRetries(orgId: string, body: RestartRequest) {
  return apiCall<RestartResult>({
    method: "POST",
    url: `${base(orgId)}/restart-max-retries`,
    data: body,
  })
}

export function getProgramPlan(orgId: string, date?: string) {
  return apiCall<PlanEntries>({
    method: "GET",
    url: `${base(orgId)}/plan`,
    params: date ? { date } : undefined,
  })
}

export function getProgramTicks(orgId: string, limit = 20) {
  return apiCall<TickRecord[]>({ method: "GET", url: `${base(orgId)}/ticks`, params: { limit } })
}

export function getProgramArms(orgId: string) {
  return apiCall<ArmResults>({ method: "GET", url: `${base(orgId)}/arms` })
}

export function getClinicDashboard(orgId: string) {
  return apiCall<ClinicDashboard>({ method: "GET", url: `/outreach/org/${orgId}/dashboard` })
}
